import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/api';
import { toast } from 'sonner';
import {
  ClipboardList, FileText, FileImage, AlertTriangle, Bell, Scale, CheckSquare,
  AlertCircle, Loader2, RefreshCw, Building2, Package, FileCheck, FileDiff
} from 'lucide-react';

// Module configuration for display
const MODULE_CONFIG = {
  si: { label: 'Site Instructions', icon: ClipboardList, color: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
  rfi: { label: 'RFIs', icon: FileText, color: 'text-purple-600', bgColor: 'bg-purple-50', borderColor: 'border-purple-200' },
  events: { label: 'Delay Events', icon: AlertTriangle, color: 'text-amber-600', bgColor: 'bg-amber-50', borderColor: 'border-amber-200' },
  notices: { label: 'Notices', icon: Bell, color: 'text-orange-600', bgColor: 'bg-orange-50', borderColor: 'border-orange-200' },
  claims: { label: 'Claims', icon: Scale, color: 'text-red-600', bgColor: 'bg-red-50', borderColor: 'border-red-200' },
  drawings: { label: 'Drawings', icon: FileImage, color: 'text-teal-600', bgColor: 'bg-teal-50', borderColor: 'border-teal-200' },
  tasks: { label: 'Tasks', icon: CheckSquare, color: 'text-green-600', bgColor: 'bg-green-50', borderColor: 'border-green-200' },
  procurement: { label: 'Procurement', icon: Package, color: 'text-sky-600', bgColor: 'bg-sky-50', borderColor: 'border-sky-200' },
  shopDrawings: { label: 'Shop Drawings', icon: FileCheck, color: 'text-indigo-600', bgColor: 'bg-indigo-50', borderColor: 'border-indigo-200' },
  meetingMinutes: { label: 'Meeting Minutes', icon: FileText, color: 'text-cyan-600', bgColor: 'bg-cyan-50', borderColor: 'border-cyan-200' },
  vos: { label: 'Variation Orders', icon: FileDiff, color: 'text-emerald-600', bgColor: 'bg-emerald-50', borderColor: 'border-emerald-200' },
  irs: { label: 'Info Required', icon: FileText, color: 'text-rose-600', bgColor: 'bg-rose-50', borderColor: 'border-rose-200' }
};

export default function AdminDashboardOverview({ projects = [] }) {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardProjectId, setDashboardProjectId] = useState('');
  
  // All module summaries in a single state object
  const [summaries, setSummaries] = useState({
    si: { total: 0, awaiting: 0, captured: 0, converted: 0 },
    rfi: { total: 0, open: 0, overdue: 0, dueThisWeek: 0 },
    events: { total: 0, open: 0, noticeDue: 0, claimDue: 0, pendingClassification: 0 },
    notices: { total: 0, draft: 0, submitted: 0, overdue: 0 },
    claims: { total: 0, draft: 0, submitted: 0, pending: 0 },
    drawings: { total: 0, issued: 0, awaiting: 0, scopeChanges: 0 },
    tasks: { total: 0, open: 0, overdue: 0, dueThisWeek: 0 },
    procurement: { total: 0, critical: 0, longLead: 0, overdue: 0 },
    shopDrawings: { total: 0, pending: 0, approved: 0, overdue: 0 },
    meetingMinutes: { total: 0, totalActions: 0, openActions: 0, overdueActions: 0 },
    vos: { total: 0, draft: 0, pending: 0, approved: 0 },
    irs: { total: 0, outstanding: 0, critical: 0, received: 0 }
  });

  // Load data for a single project
  const loadProjectData = useCallback(async (projectId) => {
    const results = {
      si: { total: 0, awaiting: 0, captured: 0, converted: 0 },
      rfi: { total: 0, open: 0, overdue: 0, dueThisWeek: 0 },
      events: { total: 0, open: 0, noticeDue: 0, claimDue: 0, pendingClassification: 0 },
      notices: { total: 0, draft: 0, submitted: 0, overdue: 0 },
      claims: { total: 0, draft: 0, submitted: 0, pending: 0 },
      drawings: { total: 0, issued: 0, awaiting: 0, scopeChanges: 0 },
      tasks: { total: 0, open: 0, overdue: 0, dueThisWeek: 0 },
      procurement: { total: 0, critical: 0, longLead: 0, overdue: 0 },
      shopDrawings: { total: 0, pending: 0, approved: 0, overdue: 0 },
      meetingMinutes: { total: 0, totalActions: 0, openActions: 0, overdueActions: 0 },
      vos: { total: 0, draft: 0, pending: 0, approved: 0 },
      irs: { total: 0, outstanding: 0, critical: 0, received: 0 }
    };

    try {
      // Load all data in parallel for this project
      const [dashboardRes, procurementRes, shopDrawingsRes, meetingMinutesRes, voRes, irsRes] = await Promise.all([
        api.get(`/dashboard/projects/${projectId}/summary?days=30`).catch(() => ({ data: null })),
        api.get(`/projects/${projectId}/procurement/stats`).catch(() => ({ data: null })),
        api.get(`/projects/${projectId}/shop-drawings/stats`).catch(() => ({ data: null })),
        api.get(`/meeting-minutes/stats/${projectId}`).catch(() => ({ data: null })),
        api.get(`/projects/${projectId}/variation-orders`).catch(() => ({ data: [] })),
        api.get(`/projects/${projectId}/irs/stats`).catch(() => ({ data: null }))
      ]);

      // Extract KPIs from dashboard summary
      const kpis = dashboardRes.data?.kpis || {};

      // Site Instructions
      const siKpis = kpis.site_instructions || {};
      results.si = {
        total: siKpis.total || 0,
        awaiting: siKpis.awaiting_clarification || 0,
        captured: siKpis.captured || 0,
        converted: siKpis.converted || 0
      };

      // RFIs
      const rfiKpis = kpis.rfis || {};
      results.rfi = {
        total: rfiKpis.total || 0,
        open: rfiKpis.open || 0,
        overdue: rfiKpis.overdue || 0,
        dueThisWeek: rfiKpis.due_this_week || 0
      };

      // Delay Events
      const eventKpis = kpis.delay_events || {};
      results.events = {
        total: eventKpis.total || 0,
        open: eventKpis.open || 0,
        noticeDue: eventKpis.notice_due || 0,
        claimDue: eventKpis.claim_due || 0,
        pendingClassification: eventKpis.pending_classification || 0
      };

      // Notices
      const noticeKpis = kpis.notices || {};
      results.notices = {
        total: noticeKpis.total || 0,
        draft: noticeKpis.draft || 0,
        submitted: noticeKpis.submitted || 0,
        overdue: noticeKpis.response_overdue || 0
      };

      // Claims
      const claimKpis = kpis.claims || {};
      results.claims = {
        total: claimKpis.total || 0,
        draft: claimKpis.draft || 0,
        submitted: claimKpis.submitted || 0,
        pending: claimKpis.determination_pending || 0
      };

      // Drawings
      const drawingKpis = kpis.drawings || {};
      results.drawings = {
        total: drawingKpis.total || 0,
        issued: drawingKpis.issued_last_period || 0,
        awaiting: drawingKpis.awaiting_acknowledgment || 0,
        scopeChanges: drawingKpis.scope_changes || 0
      };

      // Tasks
      const taskKpis = kpis.tasks || {};
      results.tasks = {
        total: taskKpis.total || 0,
        open: (taskKpis.open || 0) + (taskKpis.in_progress || 0),
        overdue: taskKpis.overdue || 0,
        dueThisWeek: taskKpis.due_this_week || 0
      };

      // Shop Drawings (from separate endpoint)
      if (shopDrawingsRes.data) {
        const sd = shopDrawingsRes.data;
        results.shopDrawings = {
          total: sd.total_packages || sd.total || 0,
          pending: sd.pending_count || sd.pending || sd.submitted || 0,
          approved: sd.approved_count || sd.approved || 0,
          overdue: (sd.overdue_with_contractor || 0) + (sd.overdue_with_consultant || 0) + (sd.overdue_resubmission || 0) || sd.overdue || 0
        };
      }

      // Procurement (from separate endpoint)
      if (procurementRes.data) {
        const proc = procurementRes.data;
        results.procurement = {
          total: proc.total_items || 0,
          critical: proc.critical_count || 0,
          longLead: proc.long_lead_count || 0,
          overdue: proc.overdue_count || 0
        };
      }

      // Meeting Minutes (from separate endpoint)
      if (meetingMinutesRes.data) {
        const mm = meetingMinutesRes.data;
        results.meetingMinutes = {
          total: mm.total_meetings || 0,
          totalActions: mm.total_actions || 0,
          openActions: mm.open_actions || 0,
          overdueActions: mm.overdue_actions || 0
        };
      }

      // Variation Orders (from separate endpoint)
      const voItems = Array.isArray(voRes.data) ? voRes.data : (voRes.data?.items || []);
      results.vos = {
        total: voItems.length,
        draft: voItems.filter(v => v.status === 'Draft').length,
        pending: voItems.filter(v => v.status === 'Pending').length,
        approved: voItems.filter(v => v.status === 'Approved').length
      };

      // IRS (from separate endpoint)
      if (irsRes.data) {
        const irs = irsRes.data;
        results.irs = {
          total: irs.total || 0,
          outstanding: irs.outstanding || 0,
          critical: irs.critical || 0,
          received: irs.received || 0
        };
      }
    } catch (error) {
      console.error(`Error loading data for project ${projectId}:`, error);
    }

    return results;
  }, []);

  // Aggregate summaries from multiple projects
  const aggregateSummaries = (summariesArray) => {
    const aggregated = {
      si: { total: 0, awaiting: 0, captured: 0, converted: 0 },
      rfi: { total: 0, open: 0, overdue: 0, dueThisWeek: 0 },
      events: { total: 0, open: 0, noticeDue: 0, claimDue: 0, pendingClassification: 0 },
      notices: { total: 0, draft: 0, submitted: 0, overdue: 0 },
      claims: { total: 0, draft: 0, submitted: 0, pending: 0 },
      drawings: { total: 0, issued: 0, awaiting: 0, scopeChanges: 0 },
      tasks: { total: 0, open: 0, overdue: 0, dueThisWeek: 0 },
      procurement: { total: 0, critical: 0, longLead: 0, overdue: 0 },
      shopDrawings: { total: 0, pending: 0, approved: 0, overdue: 0 },
      meetingMinutes: { total: 0, totalActions: 0, openActions: 0, overdueActions: 0 },
      vos: { total: 0, draft: 0, pending: 0, approved: 0 },
      irs: { total: 0, outstanding: 0, critical: 0, received: 0 }
    };

    for (const s of summariesArray) {
      // Aggregate each module
      Object.keys(aggregated).forEach(module => {
        Object.keys(aggregated[module]).forEach(key => {
          aggregated[module][key] += s[module]?.[key] || 0;
        });
      });
    }

    return aggregated;
  };

  // Main load function
  const loadAllSummaries = useCallback(async () => {
    try {
      setLoading(true);
      
      if (dashboardProjectId) {
        // Load data for specific project
        const projectData = await loadProjectData(dashboardProjectId);
        setSummaries(projectData);
      } else {
        // Load and aggregate data for ALL projects
        if (projects.length === 0) {
          setLoading(false);
          return;
        }

        const allProjectData = await Promise.all(
          projects.map(p => loadProjectData(p.id))
        );
        
        const aggregated = aggregateSummaries(allProjectData);
        setSummaries(aggregated);
      }
    } catch (error) {
      console.error('Failed to load summaries:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dashboardProjectId, projects, loadProjectData]);

  useEffect(() => {
    loadAllSummaries();
  }, [loadAllSummaries]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadAllSummaries();
  };

  // Navigation functions
  const navigateToModule = (module) => {
    if (!dashboardProjectId) {
      toast.error('Please select a project first');
      return;
    }

    const routes = {
      si: `/projects/${dashboardProjectId}?tab=si`,
      rfi: `/projects/${dashboardProjectId}?tab=rfi`,
      events: `/projects/${dashboardProjectId}?tab=events`,
      notices: `/projects/${dashboardProjectId}?tab=notices`,
      claims: `/projects/${dashboardProjectId}?tab=claims`,
      drawings: `/projects/${dashboardProjectId}?tab=drawings`,
      tasks: `/projects/${dashboardProjectId}?tab=tasks`,
      procurement: `/projects/${dashboardProjectId}/procurement`,
      shopDrawings: `/projects/${dashboardProjectId}/shop-drawings`,
      meetingMinutes: `/projects/${dashboardProjectId}/meeting-minutes`,
      vos: `/projects/${dashboardProjectId}?tab=vos`,
      irs: `/projects/${dashboardProjectId}/irs`
    };
    
    navigate(routes[module] || `/projects/${dashboardProjectId}`);
  };

  // Build stats arrays for each module card
  const getModuleStats = (module) => {
    const s = summaries[module];
    
    switch (module) {
      case 'si':
        return [
          { label: 'Total', value: s.total, filterKey: 'all' },
          { label: 'Awaiting', value: s.awaiting, filterKey: 'awaiting', warning: s.awaiting > 0 },
          { label: 'Captured', value: s.captured, filterKey: 'captured' },
          { label: 'Converted', value: s.converted, filterKey: 'converted' }
        ];
      case 'rfi':
        return [
          { label: 'Total', value: s.total, filterKey: 'all' },
          { label: 'Open', value: s.open, filterKey: 'open', warning: s.open > 0 },
          { label: 'Overdue', value: s.overdue, filterKey: 'overdue', alert: s.overdue > 0 },
          { label: 'Due Soon', value: s.dueThisWeek, filterKey: 'due_soon', warning: s.dueThisWeek > 0 }
        ];
      case 'events':
        return [
          { label: 'Total', value: s.total, filterKey: 'all' },
          { label: 'Open', value: s.open, filterKey: 'open', warning: s.open > 0 },
          { label: 'Unclassified', value: s.pendingClassification, filterKey: 'pending_classification', alert: s.pendingClassification > 0 },
          { label: 'Notice Due', value: s.noticeDue, filterKey: 'notice_due', alert: s.noticeDue > 0 }
        ];
      case 'notices':
        return [
          { label: 'Total', value: s.total, filterKey: 'all' },
          { label: 'Draft', value: s.draft, filterKey: 'draft' },
          { label: 'Submitted', value: s.submitted, filterKey: 'submitted' },
          { label: 'Overdue', value: s.overdue, filterKey: 'overdue', alert: s.overdue > 0 }
        ];
      case 'claims':
        return [
          { label: 'Total', value: s.total, filterKey: 'all' },
          { label: 'Draft', value: s.draft, filterKey: 'draft' },
          { label: 'Submitted', value: s.submitted, filterKey: 'submitted' },
          { label: 'Pending', value: s.pending, filterKey: 'pending', warning: s.pending > 0 }
        ];
      case 'drawings':
        return [
          { label: 'Total', value: s.total, filterKey: 'all' },
          { label: 'Issued', value: s.issued, filterKey: 'issued' },
          { label: 'Awaiting', value: s.awaiting, filterKey: 'awaiting', warning: s.awaiting > 0 },
          { label: 'Scope Chg', value: s.scopeChanges, filterKey: 'scope', alert: s.scopeChanges > 0 }
        ];
      case 'tasks':
        return [
          { label: 'Total', value: s.total, filterKey: 'all' },
          { label: 'Open', value: s.open, filterKey: 'open', warning: s.open > 0 },
          { label: 'Overdue', value: s.overdue, filterKey: 'overdue', alert: s.overdue > 0 },
          { label: 'Due Soon', value: s.dueThisWeek, filterKey: 'due_soon', warning: s.dueThisWeek > 0 }
        ];
      case 'procurement':
        return [
          { label: 'Total', value: s.total, filterKey: 'all' },
          { label: 'Critical', value: s.critical, filterKey: 'critical', alert: s.critical > 0 },
          { label: 'Long Lead', value: s.longLead, filterKey: 'long_lead', warning: s.longLead > 0 },
          { label: 'Overdue', value: s.overdue, filterKey: 'overdue', alert: s.overdue > 0 }
        ];
      case 'shopDrawings':
        return [
          { label: 'Total', value: s.total, filterKey: 'all' },
          { label: 'Pending', value: s.pending, filterKey: 'pending', warning: s.pending > 0 },
          { label: 'Approved', value: s.approved, filterKey: 'approved' },
          { label: 'Overdue', value: s.overdue, filterKey: 'overdue', alert: s.overdue > 0 }
        ];
      case 'meetingMinutes':
        return [
          { label: 'Meetings', value: s.total, filterKey: 'all' },
          { label: 'Actions', value: s.totalActions, filterKey: 'actions' },
          { label: 'Open', value: s.openActions, filterKey: 'open', warning: s.openActions > 0 },
          { label: 'Overdue', value: s.overdueActions, filterKey: 'overdue', alert: s.overdueActions > 0 }
        ];
      case 'vos':
        return [
          { label: 'Total', value: s.total, filterKey: 'all' },
          { label: 'Draft', value: s.draft, filterKey: 'draft' },
          { label: 'Pending', value: s.pending, filterKey: 'pending', warning: s.pending > 0 },
          { label: 'Approved', value: s.approved, filterKey: 'approved' }
        ];
      case 'irs':
        return [
          { label: 'Total', value: s.total, filterKey: 'all' },
          { label: 'Outstanding', value: s.outstanding, filterKey: 'outstanding', warning: s.outstanding > 0 },
          { label: 'Critical', value: s.critical, filterKey: 'critical', alert: s.critical > 0 },
          { label: 'Received', value: s.received, filterKey: 'received' }
        ];
      default:
        return [];
    }
  };

  // Render a module card
  const renderModuleCard = (moduleKey) => {
    const config = MODULE_CONFIG[moduleKey];
    if (!config) return null;

    const Icon = config.icon;
    const stats = getModuleStats(moduleKey);
    const hasIssues = stats.some(s => (s.alert || s.warning) && s.value > 0);
    const isClickable = !!dashboardProjectId;

    return (
      <Card 
        key={moduleKey}
        data-testid={`module-card-${moduleKey}`}
        className={`relative overflow-hidden transition-all ${
          isClickable ? 'hover:shadow-lg cursor-pointer' : 'opacity-90'
        } ${hasIssues ? `border-l-4 ${config.borderColor.replace('border-', 'border-l-')}` : ''}`}
        onClick={() => isClickable && navigateToModule(moduleKey)}
      >
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded-lg ${config.bgColor}`}>
                <Icon className={`h-5 w-5 ${config.color}`} />
              </div>
              <CardTitle className="text-base font-semibold">{config.label}</CardTitle>
            </div>
            {hasIssues && (
              <Badge className="bg-red-100 text-red-700 text-xs">
                <AlertCircle className="h-3 w-3 mr-1" />
                Action
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-2">
            {stats.map((stat, idx) => (
              <div
                key={idx}
                data-testid={`stat-${moduleKey}-${stat.filterKey}`}
                className={`text-center p-2 rounded transition-all ${
                  stat.alert && stat.value > 0 
                    ? 'bg-red-50 border border-red-200' 
                    : stat.warning && stat.value > 0
                    ? 'bg-amber-50 border border-amber-200'
                    : 'bg-slate-50'
                }`}
              >
                <p className={`text-xl font-bold ${
                  stat.alert && stat.value > 0 ? 'text-red-600' : 
                  stat.warning && stat.value > 0 ? 'text-amber-600' : 
                  config.color
                }`}>
                  {stat.value}
                </p>
                <p className={`text-xs uppercase font-medium ${
                  stat.alert && stat.value > 0 ? 'text-red-600' : 
                  stat.warning && stat.value > 0 ? 'text-amber-600' : 
                  'text-slate-500'
                }`}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6 overflow-x-hidden">
      {/* Header with Project Dropdown */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-barlow font-bold uppercase tracking-tight">Overview</h2>
          <p className="text-sm text-slate-500">
            {dashboardProjectId 
              ? projects.find(p => p.id === dashboardProjectId)?.name || 'Selected Project'
              : 'All Projects Summary'
            }
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Select value={dashboardProjectId || 'all-projects'} onValueChange={(val) => setDashboardProjectId(val === 'all-projects' ? '' : val)}>
              <SelectTrigger className="w-56" data-testid="project-selector">
                <SelectValue placeholder="Select a project..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-projects">All Projects</SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200 whitespace-nowrap hidden sm:inline">
              Select project to enable clicks
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing || loading} className="shrink-0">
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline ml-2">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          <span className="ml-3 text-slate-500">Loading dashboard data...</span>
        </div>
      )}

      {/* Unclassified Events Alert Banner */}
      {!loading && summaries.events.pendingClassification > 0 && (
        <div 
          data-testid="unclassified-events-banner"
          className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-center justify-between gap-4 animate-in fade-in slide-in-from-top-2"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-bold text-amber-900">
                {summaries.events.pendingClassification} Event{summaries.events.pendingClassification !== 1 ? 's' : ''} Awaiting Classification
              </p>
              <p className="text-xs text-amber-700">
                These events require contractual classification before notices can be generated.
                {dashboardProjectId ? '' : ' Select a project below to view and classify them.'}
              </p>
            </div>
          </div>
          {dashboardProjectId && (
            <Button
              variant="outline"
              size="sm"
              className="border-amber-400 text-amber-800 hover:bg-amber-100 shrink-0 text-xs"
              onClick={() => navigate(`/projects/${dashboardProjectId}?tab=events&filter=pending_classification`)}
              data-testid="classify-events-btn"
            >
              Classify Now
            </Button>
          )}
        </div>
      )}

      {/* Module Cards Grid */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {renderModuleCard('si')}
          {renderModuleCard('rfi')}
          {renderModuleCard('events')}
          {renderModuleCard('notices')}
          {renderModuleCard('claims')}
          {renderModuleCard('drawings')}
          {renderModuleCard('tasks')}
          {renderModuleCard('procurement')}
          {renderModuleCard('shopDrawings')}
          {renderModuleCard('meetingMinutes')}
          {renderModuleCard('vos')}
          {renderModuleCard('irs')}
        </div>
      )}

      {/* No Projects State */}
      {!loading && projects.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No Projects Found</h3>
          <p className="text-slate-500">Create a project to see dashboard data.</p>
        </div>
      )}
    </div>
  );
}
